import { defineComponent, ref, watch, computed, h, onMounted } from 'vue'
import { Select, Input, InputNumber, Switch, Form, Card, Spin, Button, Empty, Popconfirm } from 'ant-design-vue'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons-vue'
import { useField } from '@formily/vue'
import { observer } from '@formily/reactive-vue'

// 存储类型 Schema 定义
export interface StoragePropertyDef {
  type: string
  description: string
  default?: any
  enum?: string[]
}

export interface StorageSchema {
  type: string
  name: string
  description: string
  properties: Record<string, StoragePropertyDef>
}

// 获取存储 Schema 的函数类型
export type FetchStorageSchemasFunc = () => Promise<Record<string, StorageSchema>>

// 获取后端 API 基础地址
function getApiBaseUrl(): string {
  // 优先使用环境变量中的后端地址
  const envServer = (import.meta as any).env?.VITE_M7S_SERVER
  if (envServer) {
    return envServer
  }
  // 否则使用当前页面的 origin
  return `${location.protocol}//${location.host}`
}

/**
 * 动态存储配置组件
 * 根据选择的存储类型动态渲染对应的配置表单
 * 
 * 功能：
 * 1. 当没有配置存储时，显示"添加存储"按钮
 * 2. 点击按钮后显示存储类型选择器
 * 3. 选择类型后显示对应的配置表单
 * 4. 支持删除已配置的存储
 * 
 * 使用方式：
 * 1. 通过 props.schemas 直接传入 schemas
 * 2. 通过 props.fetchSchemas 传入获取 schemas 的函数
 * 3. 通过 props.apiUrl 传入获取 schemas 的 API 地址（相对路径会自动拼接后端地址）
 */
export const DynamicStorage = observer(defineComponent({
  name: 'DynamicStorage',
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    // 直接传入 schemas
    schemas: {
      type: Object as () => Record<string, StorageSchema>,
      default: () => ({}),
    },
    // 获取 schemas 的函数
    fetchSchemas: {
      type: Function as unknown as () => FetchStorageSchemasFunc,
      default: null,
    },
    // 获取 schemas 的 API 地址
    apiUrl: {
      type: String,
      default: '/api/storage/schemas',
    },
  },
  emits: ['change'],
  setup(props, { emit }) {
    const fieldRef = useField()
    const loading = ref(false)
    const schemas = ref<Record<string, StorageSchema>>({})
    
    // 是否处于添加模式
    const isAdding = ref(false)
    // 当前选中的存储类型
    const selectedType = ref<string>('')
    // 当前存储类型的配置
    const configValues = ref<Record<string, any>>({})
    // 是否已有配置
    const hasConfig = ref(false)

    // 从 value 中解析当前存储类型和配置
    const parseValue = (value: any) => {
      if (!value || typeof value !== 'object') {
        selectedType.value = ''
        configValues.value = {}
        hasConfig.value = false
        return
      }
      
      // value 格式: { "storageType": { ...config } }
      const keys = Object.keys(value)
      if (keys.length > 0) {
        selectedType.value = keys[0]
        configValues.value = value[keys[0]] || {}
        hasConfig.value = true
        isAdding.value = false
      } else {
        selectedType.value = ''
        configValues.value = {}
        hasConfig.value = false
      }
    }

    // 加载存储类型 Schema
    const loadSchemas = async () => {
      // 如果已经有 schemas，不需要加载
      if (props.schemas && Object.keys(props.schemas).length > 0) {
        schemas.value = props.schemas
        return
      }

      loading.value = true
      try {
        let result: Record<string, StorageSchema>
        
        if (props.fetchSchemas) {
          // 使用传入的函数获取
          result = await props.fetchSchemas()
        } else if (props.apiUrl) {
          // 使用 API 地址获取，如果是相对路径则拼接后端地址
          let fullUrl = props.apiUrl
          if (props.apiUrl.startsWith('/')) {
            fullUrl = getApiBaseUrl() + props.apiUrl
          }
          const response = await fetch(fullUrl)
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          result = await response.json()
        } else {
          result = {}
        }
        
        schemas.value = result
      } catch (error) {
        console.error('加载存储类型 Schema 失败:', error)
      } finally {
        loading.value = false
      }
    }

    // 初始化时解析值并加载 schemas
    onMounted(async () => {
      parseValue(props.value)
      await loadSchemas()
    })

    // 监听 value 变化
    watch(() => props.value, (newVal) => {
      parseValue(newVal)
    }, { deep: true })

    // 监听 props.schemas 变化
    watch(() => props.schemas, (newSchemas) => {
      if (newSchemas && Object.keys(newSchemas).length > 0) {
        schemas.value = newSchemas
      }
    }, { deep: true })

    // 存储类型选项
    const typeOptions = computed(() => {
      return Object.values(schemas.value).map(schema => ({
        label: schema.name,
        value: schema.type,
      }))
    })

    // 当前选中类型的 Schema
    const currentSchema = computed(() => {
      return schemas.value[selectedType.value]
    })

    // 更新值并触发 change 事件
    const updateValue = () => {
      if (!selectedType.value) {
        emit('change', {})
        return
      }
      
      const newValue = {
        [selectedType.value]: { ...configValues.value }
      }
      emit('change', newValue)
      
      // 同时更新 formily field
      const field = fieldRef.value as any
      if (field) {
        field.setValue?.(newValue)
      }
    }

    // 开始添加存储
    const handleStartAdd = () => {
      isAdding.value = true
      selectedType.value = ''
      configValues.value = {}
    }

    // 取消添加
    const handleCancelAdd = () => {
      isAdding.value = false
      selectedType.value = ''
      configValues.value = {}
    }

    // 处理存储类型变化
    const handleTypeChange = (type: string) => {
      selectedType.value = type
      // 重置配置为默认值
      const schema = schemas.value[type]
      if (schema) {
        const defaults: Record<string, any> = {}
        Object.entries(schema.properties).forEach(([key, prop]) => {
          if (prop.default !== undefined) {
            defaults[key] = prop.default
          }
        })
        configValues.value = defaults
      } else {
        configValues.value = {}
      }
      
      if (type) {
        hasConfig.value = true
        isAdding.value = false
      }
      updateValue()
    }

    // 处理配置字段变化
    const handleConfigChange = (key: string, value: any) => {
      configValues.value = {
        ...configValues.value,
        [key]: value,
      }
      updateValue()
    }

    // 删除存储配置
    const handleDelete = () => {
      selectedType.value = ''
      configValues.value = {}
      hasConfig.value = false
      isAdding.value = false
      emit('change', {})
      
      // 同时更新 formily field
      const field = fieldRef.value as any
      if (field) {
        field.setValue?.({})
      }
    }

    // 渲染配置字段
    const renderField = (key: string, prop: StoragePropertyDef) => {
      const value = configValues.value[key] ?? prop.default

      if (prop.enum && prop.enum.length > 0) {
        return h(Select, {
          value,
          options: prop.enum.map(e => ({ label: e, value: e })),
          placeholder: prop.description,
          allowClear: true,
          onChange: (v: any) => handleConfigChange(key, v),
          style: { width: '100%' },
        })
      }

      switch (prop.type) {
        case 'boolean':
          return h(Switch, {
            checked: value,
            onChange: (v: boolean) => handleConfigChange(key, v),
          })
        case 'number':
          return h(InputNumber, {
            value,
            placeholder: prop.description,
            onChange: (v: any) => handleConfigChange(key, v),
            style: { width: '100%' },
          })
        default:
          return h(Input, {
            value,
            placeholder: prop.description,
            onChange: (e: any) => handleConfigChange(key, e.target?.value ?? e),
          })
      }
    }

    // 渲染空状态（未配置存储）
    const renderEmpty = () => {
      return h('div', { class: 'dynamic-storage-empty' }, [
        h(Empty as any, {
          description: '暂未配置存储',
          image: Empty.PRESENTED_IMAGE_SIMPLE,
        }, {
          default: () => h(Button, {
            type: 'primary',
            onClick: handleStartAdd,
          }, {
            icon: () => h(PlusOutlined as any),
            default: () => '添加存储配置',
          })
        })
      ])
    }

    // 渲染添加模式（选择存储类型）
    const renderAddMode = () => {
      return h(Card, {
        title: '添加存储配置',
        size: 'small',
        extra: () => h(Button, {
          type: 'text',
          size: 'small',
          onClick: handleCancelAdd,
        }, () => '取消'),
      }, () => [
        h(Form.Item, { label: '选择存储类型' }, () => 
          h(Select, {
            value: selectedType.value || undefined,
            options: typeOptions.value,
            placeholder: '请选择存储类型',
            onChange: handleTypeChange,
            style: { width: '100%' },
          })
        ),
      ])
    }

    // 渲染配置表单
    const renderConfigForm = () => {
      if (!currentSchema.value) return null

      return h(Card, {
        title: currentSchema.value.name,
        size: 'small',
        extra: () => h(Popconfirm, {
          title: '确定要删除此存储配置吗？',
          onConfirm: handleDelete,
          okText: '确定',
          cancelText: '取消',
        }, () => h(Button, {
          type: 'text',
          danger: true,
          size: 'small',
        }, {
          icon: () => h(DeleteOutlined as any),
          default: () => '删除',
        })),
      }, () => [
        currentSchema.value.description && h('p', {
          style: { color: '#666', marginBottom: '16px', fontSize: '12px' }
        }, currentSchema.value.description),
        
        // 存储类型选择器（允许切换类型）
        h(Form.Item, { label: '存储类型' }, () => 
          h(Select, {
            value: selectedType.value,
            options: typeOptions.value,
            onChange: handleTypeChange,
            style: { width: '100%' },
          })
        ),
        
        // 配置字段
        ...Object.entries(currentSchema.value.properties).map(([key, prop]) =>
          h(Form.Item, {
            label: prop.description || key,
            key,
          }, () => renderField(key, prop))
        )
      ])
    }

    return () => {
      if (loading.value) {
        return h(Spin, { tip: '加载存储类型...' })
      }

      // 如果没有可用的存储类型
      if (Object.keys(schemas.value).length === 0) {
        return h(Empty as any, {
          description: '暂无可用的存储类型',
          image: Empty.PRESENTED_IMAGE_SIMPLE,
        })
      }

      return h('div', { class: 'dynamic-storage' }, [
        // 根据状态渲染不同的内容
        !hasConfig.value && !isAdding.value && renderEmpty(),
        isAdding.value && !selectedType.value && renderAddMode(),
        (hasConfig.value || selectedType.value) && renderConfigForm(),
      ])
    }
  },
}))

export default DynamicStorage
